import { useCallback, useEffect, useState } from "react"

import { ApolloClient, InMemoryCache, createHttpLink } from "@apollo/client"
import { setContext } from "@apollo/client/link/context"
import { fetchAuthSession } from "aws-amplify/auth"

import { getUrlEventParams, sendEvent } from "@/lib/ts/event"
import { getFragmentData, graphql } from "@/main/gql/__generated__"
import {
	SalonAllFieldFragmentDoc,
	SalonUserAllFieldFragmentDoc,
} from "@/main/gql/__generated__/graphql"

const httpLink = createHttpLink({
	uri: process.env.REACT_APP_STORE_MANAGEMENT_API_URL,
})

const authLink = setContext(async (_, { headers }) => {
	const session = await fetchAuthSession()
	const token = session.tokens?.accessToken.toString() ?? ""

	return {
		headers: {
			...headers,
			authorization: token ? `Bearer ${token}` : "",
		},
	}
})

const loginMutation = graphql(/* GraphQL */ `
	mutation Login($salonId: Int) {
		login(salonId: $salonId) {
			salon {
				...salonAllField
			}
			salons {
				...salonName
			}
			salonUser {
				...salonUserAllField
			}
		}
	}
`)

type UseLoginReturn = {
	loading: boolean
	loginData: LoginData | null
}

type LoginData = {
	currentUserID: string
	currentSalonID: number
}

// this can be used even outside of ApolloProvider
export const useLogin = (startLoading?: () => () => void): UseLoginReturn => {
	const [loginData, setLoginData] = useState<LoginData | null>(null)
	const [loading, setLoading] = useState(true)

	const login = useCallback(async (salonId?: number) => {
		const session = await fetchAuthSession()
		if (!session?.tokens) {
			console.log("fetch auth session failed")
			setLoading(false)
			return
		}

		// ApolloProvider外で使用するため、useMutationは使用しない
		const apolloClient = new ApolloClient({
			link: authLink.concat(httpLink),
			cache: new InMemoryCache(),
		})
		const res = await apolloClient.mutate({
			mutation: loginMutation,
			variables: { salonId: salonId ?? null },
		})
		apolloClient.stop()

		const data = res.data
		if (!data) {
			setLoading(false)
			alert("エラーが発生しました。しばらく経ってから再度お試しください。")
			return
		}
		const salon = getFragmentData(SalonAllFieldFragmentDoc, data.login.salon)
		const salonUser = getFragmentData(
			SalonUserAllFieldFragmentDoc,
			data.login.salonUser
		)

		// ログイン完了
		setLoading(false)
		setLoginData({ currentSalonID: salon.id, currentUserID: salonUser.id })

		// fe-event: login（store_management 専用 — notification_log_id は未請求通知メールのURLパラメータ）
		sendEvent(salonUser.id, "login", {
			salon_id: salon.id,
			...getUrlEventParams([
				"notification_log_id",
				"without_bundle_claim_notification_log_id",
			]),
		})
	}, [])

	useEffect(() => {
		const asyncFunc = async () => {
			// Reactのrouting外なのでuseParamは使用できない
			const urlParams = new URLSearchParams(window.location.search)
			const sidParam = urlParams.get("sid")
			let requestedSalonId: number | undefined

			if (sidParam) {
				const decodedSid = atob(sidParam)
				const parsedId = parseInt(decodedSid, 10)

				if (!isNaN(parsedId) && parsedId > 0) {
					requestedSalonId = parsedId
				} else {
					return
				}
			}

			await login(requestedSalonId)
		}
		asyncFunc()
	}, [startLoading, login])

	return {
		loading,
		loginData,
	}
}

export default useLogin
